export interface MarchYaknessTimingWindow {
	window_start_at: number;
	window_end_at: number;
	finalization_end_at: number;
}

export type MarchYaknessPhase = 'upcoming' | 'live' | 'finalizing' | 'completed';

export const MARCH_YAKNESS_SESSION_SIGNATURE_HEADER = 'x-client-build-time';
export const MARCH_YAKNESS_SESSION_SIGNATURE_VERSION = 'v1';
export const MARCH_YAKNESS_SESSION_SIGNATURE_LENGTH = 16;

const MARCH_YAKNESS_SESSION_SIGNATURE_SECRETS = {
	v1: 'march_yakness_session_signature_v1_a7d90d3e2b1c4f68',
} as const;

function toNow(nowEpochSeconds?: number) {
	return nowEpochSeconds ?? Math.floor(Date.now() / 1000);
}

function getMarchYaknessSessionSignatureSecret(version: string): string {
	const secret =
		MARCH_YAKNESS_SESSION_SIGNATURE_SECRETS[
			version as keyof typeof MARCH_YAKNESS_SESSION_SIGNATURE_SECRETS
		];
	if (!secret) {
		throw new Error(
			`Unsupported March Yakness session signature version: ${version}. Supported versions: ${Object.keys(MARCH_YAKNESS_SESSION_SIGNATURE_SECRETS).join(', ')}`,
		);
	}
	return secret;
}

function toHex(buffer: ArrayBuffer): string {
	return Array.from(new Uint8Array(buffer), (value) => value.toString(16).padStart(2, '0')).join('');
}

export function getMarchYaknessPhase(
	window: MarchYaknessTimingWindow,
	nowEpochSeconds?: number,
): MarchYaknessPhase {
	const now = toNow(nowEpochSeconds);
	if (now < window.window_start_at) return 'upcoming';
	if (now < window.window_end_at) return 'live';
	if (now < window.finalization_end_at) return 'finalizing';
	return 'completed';
}

export function isMarchYaknessPlayable(
	window: MarchYaknessTimingWindow,
	nowEpochSeconds?: number,
): boolean {
	return getMarchYaknessPhase(window, nowEpochSeconds) === 'live';
}

export function getMarchYaknessSecondsUntilStart(
	window: MarchYaknessTimingWindow,
	nowEpochSeconds?: number,
): number {
	return Math.max(0, window.window_start_at - toNow(nowEpochSeconds));
}

export function getMarchYaknessSecondsUntilWindowEnd(
	window: MarchYaknessTimingWindow,
	nowEpochSeconds?: number,
): number {
	return Math.max(0, window.window_end_at - toNow(nowEpochSeconds));
}

export function getMarchYaknessTimeRemaining(
	window: MarchYaknessTimingWindow,
	nowEpochSeconds?: number,
): number {
	const phase = getMarchYaknessPhase(window, nowEpochSeconds);
	if (phase === 'live') return getMarchYaknessSecondsUntilWindowEnd(window, nowEpochSeconds);
	if (phase === 'upcoming') return getMarchYaknessSecondsUntilStart(window, nowEpochSeconds);
	return 0;
}

export async function generateMarchYaknessSessionSignature({
	authToken,
	sessionUuid,
	version = MARCH_YAKNESS_SESSION_SIGNATURE_VERSION,
}: {
	authToken: string;
	sessionUuid: string;
	version?: keyof typeof MARCH_YAKNESS_SESSION_SIGNATURE_SECRETS;
}): Promise<string> {
	const normalizedAuthToken = authToken.trim();
	const normalizedSessionUuid = sessionUuid.trim();
	if (!normalizedAuthToken || !normalizedSessionUuid) {
		throw new Error('March Yakness session signature requires authToken and sessionUuid.');
	}

	const subtle = globalThis.crypto?.subtle;
	if (!subtle) {
		throw new Error('Web Crypto is unavailable for March Yakness session signature generation.');
	}

	const payload = `${normalizedAuthToken}:${normalizedSessionUuid}`;
	const key = await subtle.importKey(
		'raw',
		new TextEncoder().encode(getMarchYaknessSessionSignatureSecret(version)),
		{ name: 'HMAC', hash: 'SHA-256' },
		false,
		['sign'],
	);
	const digest = await subtle.sign('HMAC', key, new TextEncoder().encode(payload));
	const signature = toHex(digest).slice(0, MARCH_YAKNESS_SESSION_SIGNATURE_LENGTH);
	return `${version}.${signature}`;
}
